import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ringArt from '../../../assets/img/portal-ring.png';
import OpenPortalButton from '../../wall/OpenPortalButton';
import { T, RADIUS, BODY_FONT } from '../messagesModel';
import { prefersReducedMotion } from '../../transitions/portalMotion';
import { kickerFor, summaryLine, whatItFound, portalSpec, eventSpec, EASE } from './planModel';

const X = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
);
/** Lucide `ticket` (a substitute, flagged in the handoff). */
const TICKET = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden><path d="M3 7h18v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4z" /><path d="M14 7v10" strokeDasharray="2 2" /></svg>
);

/**
 * The plan sheet (handoff §2): REVIEW, or a tap on a highlight. A bottom sheet under 640px (radius
 * 28, a grabber, 420ms up), a 500px centred modal above (rising 14px from .97 over 320ms), the
 * scrim fading in over 240ms. Kicker · title · summary · WHAT IT FOUND · MAKE IT A → the Portal
 * button (ring pops, pill grows, two cyan pings) and the outlined CREATE AN EVENT.
 *
 * Leaving: → Portal fades the sheet and grows a black disc with the spinning ring from the
 * button until it fills the screen, then `onPortal()` navigates. → Event slides the sheet down
 * (phone) or fades it (desktop), then `onEvent()` opens the review.
 */
export default function PlanSheet({ draft, viewerUserId, mobile = false, onClose, onPortal, onEvent }) {
  const reduced = prefersReducedMotion();
  const [leaving, setLeaving] = useState(null); // 'portal' | 'event' | 'close'
  const [burst, setBurst] = useState(null);     // { x, y }
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!draft || typeof document === 'undefined') return null;

  const later = (fn, ms) => timers.current.push(setTimeout(fn, reduced ? 0 : ms));
  const pickPortal = (e) => {
    if (leaving) return;
    const btn = e?.currentTarget;
    const circle = btn?.querySelector?.('[data-portal-circle]') || btn;
    const r = circle?.getBoundingClientRect?.();
    setBurst(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : { x: window.innerWidth / 2, y: window.innerHeight * 0.7 });
    setLeaving('portal');
    later(() => onPortal?.(), 640);
  };
  const pickEvent = () => {
    if (leaving) return;
    setLeaving('event');
    later(() => onEvent?.(), 260);
  };

  const rows = whatItFound(draft, viewerUserId);
  const scrimAnim = reduced ? 'none' : (leaving ? 'prPlanFade 240ms linear reverse both' : 'prPlanFade 240ms linear both');
  const panelAnim = reduced ? 'none'
    : leaving === 'event' ? (mobile ? 'prPlanSheetDown 240ms cubic-bezier(.4,0,1,1) both' : 'prPlanModalOut 220ms cubic-bezier(.4,0,1,1) both')
      : leaving === 'portal' ? 'prPlanModalOut 300ms cubic-bezier(.4,0,1,1) 160ms both'
        : (mobile ? `prPlanSheetUp 420ms ${EASE} both` : `prPlanModalIn 320ms ${EASE} both`);

  return createPortal(
    <div data-plan-sheet role="dialog" aria-modal="true" aria-label="Plan detected" className="pr-overlay-root font-mono" style={{ position: 'fixed', inset: 0, zIndex: 120 }}>
      <div data-plan-anim onClick={() => !leaving && onClose?.()} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)', animation: scrimAnim }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', alignItems: mobile ? 'flex-end' : 'center', padding: mobile ? 0 : 24 }}>
        <div
          data-plan-anim
          data-plan-panel
          style={{
            pointerEvents: 'auto', width: '100%', maxWidth: mobile ? '100%' : 500, background: T.white, color: T.ink,
            borderRadius: mobile ? '28px 28px 0 0' : RADIUS.modal, padding: mobile ? '18px 20px calc(24px + env(safe-area-inset-bottom, 0px))' : '24px 28px 28px',
            display: 'flex', flexDirection: 'column', gap: 16, boxSizing: 'border-box', maxHeight: '100%', overflowY: 'auto', animation: panelAnim,
          }}
        >
          {mobile && <span aria-hidden style={{ alignSelf: 'center', width: 40, height: 4, borderRadius: 2, background: T.dayPill, marginTop: -6 }} />}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span data-plan-kicker style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: T.grey500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kickerFor(draft)}</span>
            <button type="button" onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: '50%', border: 0, background: T.off, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', flexShrink: 0 }}>{X}</button>
          </div>
          <div data-plan-anim style={{ display: 'flex', flexDirection: 'column', gap: 6, animation: reduced ? 'none' : `prPlanRise 380ms ${EASE} 160ms both` }}>
            <span data-plan-title style={{ fontFamily: BODY_FONT, fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{draft.title}</span>
            <span style={{ fontFamily: BODY_FONT, fontSize: 14, lineHeight: 1.45, color: T.grey600 }}>{summaryLine(draft)}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', borderRadius: RADIUS.row, background: T.canvas, padding: '4px 14px' }}>
            {rows.map((r, i) => (
              <div key={r.key} data-plan-anim data-plan-row={r.key} style={{ display: 'grid', gridTemplateColumns: '72px minmax(0,1fr)', gap: 12, alignItems: 'baseline', padding: '10px 0', borderTop: i ? '2px solid #EAEAEA' : 0, animation: reduced ? 'none' : `prPlanRise 340ms ${EASE} ${260 + i * 60}ms both` }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: T.grey500 }}>{r.key}</span>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', lineHeight: 1.45, color: r.missing ? T.grey400 : T.ink }}>{r.missing ? 'NOT MENTIONED' : r.value}</span>
              </div>
            ))}
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: T.grey500, marginTop: 2 }}>MAKE IT A</span>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
              <span data-plan-anim style={{ display: 'block', animation: reduced ? 'none' : `prPlanPop 360ms ${EASE} 380ms both` }}>
                <OpenPortalButton size={52} expanded onClick={pickPortal} label="OPEN A PORTAL" style={{ opacity: leaving === 'portal' ? 0 : 1, transition: 'opacity 160ms' }}>
                  <span style={{ position: 'relative', display: 'flex' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="7" r="2" /><path d="M12 9V21" /><path d="M8.4 3.6a5.5 5.5 0 0 0 0 6.8" /><path d="M15.6 3.6a5.5 5.5 0 0 1 0 6.8" /></svg>
                  </span>
                </OpenPortalButton>
                {!reduced && <span aria-hidden style={{ position: 'absolute', width: 52, height: 52, marginLeft: -52, borderRadius: '50%', border: '2px solid #00FFFF', pointerEvents: 'none', opacity: 0, animation: 'prPlanPing 1.2s cubic-bezier(0,0,.2,1) 900ms 2' }} />}
              </span>
              <span data-plan-portal-spec style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: T.grey500, lineHeight: 1.5 }}>{portalSpec(draft)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
              <button
                type="button"
                data-plan-anim
                data-plan-event
                onClick={pickEvent}
                className="hover:bg-black hover:text-white"
                style={{ height: 52, padding: '0 22px 0 18px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.white, color: T.ink, display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit', animation: reduced ? 'none' : `prPlanRise 380ms ${EASE} 700ms both`, transition: 'background-color 200ms, color 200ms' }}
              >
                {TICKET}
                CREATE AN EVENT
              </button>
              <span data-plan-event-spec style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: T.grey500, lineHeight: 1.5 }}>{eventSpec(draft)}</span>
            </div>
          </div>
        </div>
      </div>
      {burst && (
        <span data-plan-anim data-plan-burst aria-hidden style={{ position: 'fixed', zIndex: 130, width: 52, height: 52, margin: '-26px 0 0 -26px', borderRadius: '50%', background: '#000', pointerEvents: 'none', left: burst.x, top: burst.y, animation: reduced ? 'none' : 'prPlanBurst 620ms cubic-bezier(.7,0,.2,1) 60ms both', transform: reduced ? 'scale(46)' : undefined }}>
          <span style={{ position: 'absolute', inset: -3, borderRadius: '50%', backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: 'prPlanSpin 1.2s linear infinite' }} />
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#000' }} />
        </span>
      )}
    </div>,
    document.body,
  );
}
